import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { enumKeyByValue, instrumentOwnersSortFn } from '@app/shared/helpers';
import { InstrumentType, OwnerType } from '@app/shared/models/contracts/enums/shared-enums';
import classNames from 'classnames';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import { slugifyStrings } from '@app/shared/helpers/formatting/slugify';
import {
  filterHistoricEventDatesFromFirstEvent,
  getMaxColumnCount,
} from '../../../../utils/OpmInputHelper';
import OpmInputHistoricCells from '../../../OpmInputHistoricCells';

export const SummaryShareholderDebt = () => {
  const { l } = useLocale();
  const project = useAppSelector((state) => state.project.projectDraft);
  const opmInputResults = useAppSelector((state) => state.opmCalculation.calculatedInputs);
  const showHistoricDataColumns = useAppSelector(
    (state) => state.uiValues.userSelections.opmInputs.showHistoricDataColumns
  );
  const historicColumnCount = Object.keys(
    filterHistoricEventDatesFromFirstEvent(opmInputResults?.historicEventDates ?? [])
  ).length;

  if (!opmInputResults || !opmInputResults.instrumentInformation) {
    return <></>;
  }

  const debtInstruments = Object.values(opmInputResults.instrumentInformation.instruments).filter(
    (ins) => ins.instrumentType !== enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity)
  );
  const slnInstruments = debtInstruments.filter(
    (ins) =>
      ins.instrumentType === enumKeyByValue(InstrumentType, InstrumentType.ShareholderLoanNotes)
  );
  const prefSharesInstruments = debtInstruments.filter(
    (ins) => ins.instrumentType === enumKeyByValue(InstrumentType, InstrumentType.PreferredShares)
  );

  const maxColspanCount =
    project.opmInput.forecastYears !== null
      ? getMaxColumnCount(
          historicColumnCount,
          project.opmInput.forecastYears,
          showHistoricDataColumns
        )
      : 0;
  const maxDataColspanCount = maxColspanCount - 1;

  const getUniqueOwnersByType = (instrumentType: keyof typeof InstrumentType) => {
    const ownersAcrossType = Object.values(opmInputResults.forecastDates).flatMap((d) =>
      Object.entries(d.shareholderDebtSummary).filter(([key, _]) => key === instrumentType)
    );

    const uniqueOwners = new Set(
      ownersAcrossType
        .flatMap(([_, ownerDetails]) => Object.keys(ownerDetails))
        .map((o) => o as keyof typeof OwnerType)
    );

    return uniqueOwners;
  };

  const ownersByType = {
    [enumKeyByValue(InstrumentType, InstrumentType.ShareholderLoanNotes)]: getUniqueOwnersByType(
      enumKeyByValue(InstrumentType, InstrumentType.ShareholderLoanNotes)
    ),
    [enumKeyByValue(InstrumentType, InstrumentType.PreferredShares)]: getUniqueOwnersByType(
      enumKeyByValue(InstrumentType, InstrumentType.PreferredShares)
    ),
  };

  const renderSummarySection = (
    instrumentType: keyof typeof InstrumentType,
    title: string
  ): JSX.Element => {
    return (
      <>
        <tr className="table-primary__row--header">
          <th className="table-primary__cell--header-tertiary">{title}</th>
          <td className="table-primary__cell--header-tertiary" colSpan={maxDataColspanCount} />
        </tr>
        {[...ownersByType[instrumentType]].sort(instrumentOwnersSortFn).map((ownerType) => {
          const ownerTypeValue = OwnerType[ownerType as keyof typeof OwnerType];
          return (
            <tr key={ownerType}>
              <th
                className={classNames(
                  'table-primary__cell--vertical-separator table-primary__cell--indentation'
                )}>
                {ownerType} {l('_Total')}
              </th>
              <OpmInputHistoricCells
                indexIdentifier={`${title}-${ownerType}`}
                renderCell={(historicDate, value) => (
                  <CellValue
                    {...amountRules}
                    dataTestId={slugifyStrings(
                      title,
                      ownerTypeValue,
                      'summary-values',
                      historicDate ?? ''
                    )}
                    value={value.shareholderDebtSummary[instrumentType]?.[ownerType] ?? 0}
                  />
                )}
              />
              <td>
                <CellValue
                  {...amountRules}
                  dataTestId={slugifyStrings(title, ownerTypeValue, 'historical-summary-values')}
                  value={
                    opmInputResults.valuationDate.shareholderDebtSummary[instrumentType]?.[
                      ownerType
                    ] ?? 0
                  }
                />
              </td>
              {Object.entries(opmInputResults.forecastDates).map(([forecastDate, value]) => (
                <td key={forecastDate}>
                  <CellValue
                    {...amountRules}
                    dataTestId={slugifyStrings(
                      title,
                      ownerTypeValue,
                      'summary-values',
                      forecastDate ?? ''
                    )}
                    value={value.shareholderDebtSummary[instrumentType]?.[ownerType] ?? 0}
                  />
                </td>
              ))}
              <td className={classNames('table-primary__cell--vertical-separator')} />
            </tr>
          );
        })}

        <tr className="table-primary__row--plain">
          <th className="table-primary__cell--vertical-separator">
            <strong>{l('_SummaryTitleTotal', { summaryTitle: title })}</strong>
          </th>
          <OpmInputHistoricCells
            indexIdentifier={`${title}-total`}
            renderCell={(historicEventDate, value) => (
              <CellValue
                {...amountRules}
                dataTestId={slugifyStrings(title, historicEventDate ?? '', 'total')}
                value={value.shareholderDebtTotalByType[instrumentType] ?? 0}
                strong
              />
            )}
          />
          <td>
            <CellValue
              {...amountRules}
              dataTestId={slugifyStrings(title, 'history-total')}
              value={opmInputResults.valuationDate.shareholderDebtTotalByType[instrumentType] ?? 0}
              strong
            />
          </td>
          {Object.entries(opmInputResults.forecastDates).map(([forecastDate, value]) => (
            <td key={forecastDate + 'sln-totals-sum'}>
              <CellValue
                {...amountRules}
                dataTestId={slugifyStrings(title, forecastDate ?? '', 'total')}
                value={value.shareholderDebtTotalByType[instrumentType] ?? 0}
                strong
              />
            </td>
          ))}
          <td />
        </tr>
      </>
    );
  };

  const showSlnSection = slnInstruments.length > 0;
  const showPrefSharesSection = prefSharesInstruments.length > 0;

  return (
    <>
      <tr>
        <th
          className="table-primary__cell--header table-primary__cell--header-primary"
          data-testid="summary-shareholder-debt">
          {l('_SummaryShareholderDebt')}
        </th>
        <td
          className="table-primary__cell--header table-primary__cell--header-primary"
          colSpan={maxDataColspanCount}
        />
      </tr>
      {showPrefSharesSection &&
        renderSummarySection(
          enumKeyByValue(InstrumentType, InstrumentType.PreferredShares),
          l('_PreferenceShares')
        )}
      {showSlnSection &&
        renderSummarySection(
          enumKeyByValue(InstrumentType, InstrumentType.ShareholderLoanNotes),
          l('_ShareholderLoanNotes')
        )}
    </>
  );
};
